import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { ADMIN_GOOGLE_CLIENT_ID } from "@/lib/env";

// ---------------------------------------------------------------------------
// Shared chrome + Google sign-in gate for the admin surface
// (admin.digavouch.com — /tenants, /inquiries). Extracted from Tenants.tsx
// when the R1 inquiry-timeline page arrived so both pages share one gate.
//
// Auth: "Sign in with Google" (dedicated OAuth client) → ID token → sent as
// Authorization: Bearer to /api/v1/admin/*. Access is gated server-side to
// the allowlist (patrick@/juan@); these components just surface the result.
// ---------------------------------------------------------------------------

// Minimal Google Identity Services typing (window.google is untyped).
interface GsiId {
  initialize(cfg: { client_id: string; callback: (resp: { credential: string }) => void }): void;
  renderButton(parent: HTMLElement, opts: Record<string, unknown>): void;
  disableAutoSelect(): void;
}
type GoogleGsi = { accounts: { id: GsiId } };
function gsi(): GoogleGsi | null {
  return (window as unknown as { google?: GoogleGsi }).google ?? null;
}

function useGoogleSignIn() {
  const [idToken, setIdToken] = useState<string | null>(null);
  const buttonRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!ADMIN_GOOGLE_CLIENT_ID || idToken) return;

    const init = () => {
      const g = gsi();
      if (!g || !buttonRef.current) return;
      g.accounts.id.initialize({
        client_id: ADMIN_GOOGLE_CLIENT_ID,
        callback: (resp) => setIdToken(resp.credential),
      });
      g.accounts.id.renderButton(buttonRef.current, {
        theme: "filled_blue",
        size: "large",
        text: "signin_with",
        shape: "pill",
      });
    };

    if (gsi()) {
      init();
      return;
    }
    const existing = document.getElementById("gsi-client");
    if (existing) {
      existing.addEventListener("load", init);
      return () => existing.removeEventListener("load", init);
    }
    const script = document.createElement("script");
    script.id = "gsi-client";
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = init;
    document.body.appendChild(script);
  }, [idToken]);

  const signOut = useCallback(() => {
    gsi()?.accounts.id.disableAutoSelect();
    setIdToken(null);
  }, []);

  return { idToken, signOut, buttonRef };
}

const NAV = [
  { to: "/tenants", label: "Tenants" },
  { to: "/inquiries", label: "Inquiries" },
];

export function AdminShell({ children, wide = false }: { children: ReactNode; wide?: boolean }) {
  const { pathname } = useLocation();
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-[#1a1a2e] px-8 py-5 flex items-center gap-8">
        <h1 className="text-white text-xl font-bold">DigaVouch · Admin</h1>
        <nav className="flex gap-4">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              className={`text-sm ${
                pathname.startsWith(n.to) ? "text-white font-semibold underline" : "text-slate-300 hover:text-white"
              }`}
            >
              {n.label}
            </Link>
          ))}
        </nav>
      </header>
      <main className={`${wide ? "max-w-7xl" : "max-w-5xl"} mx-auto p-8`}>{children}</main>
    </div>
  );
}

/**
 * The sign-in gate both admin pages share. Renders the not-configured
 * warning, the Google button, or — once signed in — `children(idToken,
 * signOut)`.
 */
export function AdminGate({
  wide = false,
  children,
}: {
  wide?: boolean;
  children: (idToken: string, signOut: () => void) => ReactNode;
}) {
  const { idToken, signOut, buttonRef } = useGoogleSignIn();

  if (!ADMIN_GOOGLE_CLIENT_ID) {
    return (
      <AdminShell wide={wide}>
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-4">
          Admin sign-in isn't configured: <code>VITE_ADMIN_GOOGLE_CLIENT_ID</code> is not set for this
          environment.
        </p>
      </AdminShell>
    );
  }

  if (!idToken) {
    return (
      <AdminShell wide={wide}>
        <p className="text-sm text-slate-600 mb-4">Sign in with your DigaVouch Google account.</p>
        <div ref={buttonRef} />
      </AdminShell>
    );
  }

  return <AdminShell wide={wide}>{children(idToken, signOut)}</AdminShell>;
}
